import React, { useContext, useState } from "react";
import { useMutation } from "@tanstack/react-query";

//App
import { TemplateEmailContext } from "./TemplateEmailContainer";
import { TemplateEmailFormContainerProps } from "../../../types/template";
import { TemptEmailViewModel } from "../../../viewModels/temptEmailViewModel";
import { templateDto } from "../../../app/model/api/types/dto/templateDto";

//Common
import { EMPTY_GUID } from "../../../utility/constants";
import { apiErrorMessage } from "../../../utility/lib/error";
import { SnackbarState } from "../../../types";
import { Snackbar } from "../../common/VFX-UI";
import templateApi from "../../../app/model/api/templateApi";
import TemplateEmailForm from "./TemplateEmailForm";

export const TemplateEmailFormContainer: React.FC<
    TemplateEmailFormContainerProps
> = (props) => {
    const { onClose, onShowSuccess } = props;
    const templateContext = useContext(TemplateEmailContext);

    const [snackbar, setSnackbar] = useState<SnackbarState>({
        showErrorSnackbar: false,
        errorMessage: "",
    });

    const successMessage = templateContext?.id
        ? "Template updated"
        : "New Template added";

    const onShowErrorSnackbar = (errorMessage: string) => {
        setSnackbar({
            showErrorSnackbar: true,
            errorMessage,
        });
    };

    const onCloseSnackbar = () => {
        setSnackbar({
            showErrorSnackbar: false,
            errorMessage: "",
        });
    };

    const { status, mutate } = useMutation<void, Error, templateDto>({
        mutationFn: async (payload: templateDto) => {
            const response = await templateApi.saveEmailTemplate(payload);
            const errorMessage = apiErrorMessage(response.errorMessages);
            if (errorMessage) {
                onShowErrorSnackbar(errorMessage);
            } else {
                onShowSuccess(successMessage);
                templateContext?.refetchGrid();
            }
        },
        onError: (error) => onShowErrorSnackbar(error.message || "Unable to save the email template."),
    });

    const handleOnSubmit = async (values: TemptEmailViewModel) => {
        const template: templateDto = { ...values };
        mutate(template);
    };

    const newDate = new Date();

    const initialValues = templateContext?.id
        ? new TemptEmailViewModel(
            templateContext?.data?.id ?? EMPTY_GUID,
            templateContext?.data?.parentTemplateId ?? null,
            templateContext?.data?.communicationTypeId ?? null,
            templateContext?.data?.fromEmailAddress ?? null,
            templateContext?.data?.fromName ?? null,
            templateContext?.data?.maxProcessTime ?? null,
            templateContext?.data?.minProcessTime ?? null,
            templateContext?.data?.subject ?? null,
            templateContext?.data?.templateDescription ?? null,
            templateContext?.data?.templateName ?? null,
            templateContext?.data?.textContent ?? null,
            templateContext?.data?.htmlContent ?? null,
            templateContext?.data?.isActive ?? null,
            templateContext?.data?.isArchived ?? null,
            templateContext?.data?.isDeleted ?? null,
            templateContext?.data?.lockLifespanSeconds ?? null,
            templateContext?.data?.requestLifespanSeconds ?? null,
            templateContext?.data?.maxAttempts ?? null,
            templateContext?.data?.periodEnd ?? null,
            templateContext?.data?.periodStart ?? null,
            templateContext?.data?.recordVersion ?? null
        )
        : new TemptEmailViewModel(
            EMPTY_GUID,
            "",
            "",
            "",
            "",
            "18:00:00",
            "09:00:00",
            "",
            "",
            "",
            "",
            "",
            true,
            false,
            false,
            300,
            1500000,
            15,
            "9999-12-31T23:59:59.9999999",
            newDate.toISOString(),
            null
        );

    return (
        <>
            <TemplateEmailForm
                initialVal={{...initialValues}}
                status={status}
                handleOnSubmit={handleOnSubmit}
                onClose={onClose}
            />
            {snackbar.showErrorSnackbar && (
                <Snackbar
                    open={true}
                    message={snackbar.errorMessage}
                    handleClose={onCloseSnackbar}
                    severity="danger"
                />
            )}
        </>
    );
};
